
"use client"

import { useEffect, useRef } from "react"
import { motion } from "framer-motion"

export const AIVisualization = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!canvasRef.current) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    // Set canvas dimensions
    const setCanvasDimensions = () => {
      const dpr = window.devicePixelRatio || 1
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      ctx.scale(dpr, dpr)
      canvas.style.width = `${rect.width}px`
      canvas.style.height = `${rect.height}px`
    }

    setCanvasDimensions()
    window.addEventListener("resize", setCanvasDimensions)

    // Animation variables
    let animationFrame: number
    let time = 0
    const nodes: Node[] = []
    const connections: Connection[] = []
    const dataParticles: DataParticle[] = []

    // Create nodes
    const createNodes = () => {
      // Clear existing nodes
      nodes.length = 0
      connections.length = 0

      // Create central AI node
      const centerX = canvas.width / (2 * (window.devicePixelRatio || 1))
      const centerY = canvas.height / (2 * (window.devicePixelRatio || 1))

      const aiNode: Node = {
        x: centerX,
        y: centerY,
        radius: 40,
        color: "rgba(59, 130, 246, 0.9)",
        type: "ai", // Explicitly use "ai" as a literal
        connections: [],
        pulsing: true,
        pulseSize: 0,
        pulseOpacity: 0.5,
      }
      nodes.push(aiNode)

      // Create data source nodes
      const dataSourceCount = 5
      const dataRadius = 180
      for (let i = 0; i < dataSourceCount; i++) {
        const angle = ((Math.PI * 2) / dataSourceCount) * i
        const x = centerX + Math.cos(angle) * dataRadius
        const y = centerY + Math.sin(angle) * dataRadius

        const dataNode: Node = {
          x,
          y,
          radius: 20,
          color: "rgba(99, 102, 241, 0.8)",
          type: "data", // Explicitly use "data" as a literal
          connections: [],
          pulsing: Math.random() > 0.5,
          pulseSize: 0,
          pulseOpacity: 0.5,
        }
        nodes.push(dataNode)

        // Connect to AI node
        connections.push({
          from: dataNode,
          to: aiNode,
          width: 2,
          color: "rgba(99, 102, 241, 0.4)",
          particles: [],
        })
      }

      // Create output nodes
      const outputCount = 4
      const outputRadius = 180
      for (let i = 0; i < outputCount; i++) {
        const angle = ((Math.PI * 2) / outputCount) * i + Math.PI / outputCount
        const x = centerX + Math.cos(angle) * outputRadius
        const y = centerY + Math.sin(angle) * outputRadius

        const outputNode: Node = {
          x,
          y,
          radius: 25,
          color: "rgba(16, 185, 129, 0.8)",
          type: "output", // Explicitly use "output" as a literal
          connections: [],
          pulsing: Math.random() > 0.5,
          pulseSize: 0,
          pulseOpacity: 0.5,
        }
        nodes.push(outputNode)

        // Connect from AI node
        connections.push({
          from: aiNode,
          to: outputNode,
          width: 2,
          color: "rgba(16, 185, 129, 0.4)",
          particles: [],
        })
      }

      // Create data particles
      for (const connection of connections) {
        const particleCount = 3
        for (let i = 0; i < particleCount; i++) {
          const progress = i / particleCount
          dataParticles.push({
            connection,
            progress,
            speed: 0.003 + Math.random() * 0.002,
            size: 3 + Math.random() * 2,
            opacity: 0.7 + Math.random() * 0.3,
          })
        }
      }
    }

    createNodes()
    window.addEventListener("resize", createNodes)

    // Animation loop
    const animate = () => {
      if (!ctx) return

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Update time
      time += 0.01

      // Draw grid background
      drawGrid(ctx)

      // Draw connections
      for (const connection of connections) {
        drawConnection(ctx, connection)
      }

      // Update and draw data particles
      for (const particle of dataParticles) {
        // Update position
        particle.progress += particle.speed
        if (particle.progress > 1) {
          particle.progress = 0
        }

        // Draw particle
        drawParticle(ctx, particle)
      }

      // Draw nodes
      for (const node of nodes) {
        drawNode(ctx, node, time)
      }

      // Continue animation
      animationFrame = requestAnimationFrame(animate)
    }

    // Draw grid
    const drawGrid = (ctx: CanvasRenderingContext2D) => {
      const width = canvas.width / (window.devicePixelRatio || 1)
      const height = canvas.height / (window.devicePixelRatio || 1)

      ctx.strokeStyle = "rgba(148, 163, 184, 0.1)"
      ctx.lineWidth = 1

      // Horizontal lines
      const rowCount = 20
      for (let i = 0; i <= rowCount; i++) {
        const y = (height / rowCount) * i
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(width, y)
        ctx.stroke()
      }

      // Vertical lines
      const colCount = 40
      for (let i = 0; i <= colCount; i++) {
        const x = (width / colCount) * i
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, height)
        ctx.stroke()
      }
    }

    // Draw node
    const drawNode = (ctx: CanvasRenderingContext2D, node: Node, time: number) => {
      ctx.save()

      // Draw pulse effect
      if (node.pulsing) {
        node.pulseSize = 20 + Math.sin(time * 2) * 10
        node.pulseOpacity = 0.2 + Math.sin(time * 2) * 0.1

        const gradient = ctx.createRadialGradient(
          node.x,
          node.y,
          node.radius,
          node.x,
          node.y,
          node.radius + node.pulseSize,
        )
        gradient.addColorStop(0, node.color)
        gradient.addColorStop(1, "rgba(0, 0, 0, 0)")

        ctx.fillStyle = gradient
        ctx.globalAlpha = node.pulseOpacity
        ctx.beginPath()
        ctx.arc(node.x, node.y, node.radius + node.pulseSize, 0, Math.PI * 2)
        ctx.fill()
        ctx.globalAlpha = 1
      }

      // Draw node
      ctx.fillStyle = node.color
      ctx.shadowColor = node.color
      ctx.shadowBlur = 10
      ctx.beginPath()
      ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2)
      ctx.fill()

      // Draw node icon
      ctx.fillStyle = "rgba(255, 255, 255, 0.9)"
      ctx.shadowBlur = 0

      if (node.type === "ai") {
        // Draw AI symbol
        ctx.font = "bold 24px sans-serif"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText("AI", node.x, node.y)
      } else if (node.type === "data") {
        // Draw data symbol
        ctx.font = "bold 16px sans-serif"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText("D", node.x, node.y)
      } else if (node.type === "output") {
        // Draw output symbol
        ctx.font = "bold 16px sans-serif"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText("$", node.x, node.y)
      }

      ctx.restore()
    }

    // Draw connection
    const drawConnection = (ctx: CanvasRenderingContext2D, connection: Connection) => {
      const { from, to, width, color } = connection

      // Draw connection line
      ctx.strokeStyle = color
      ctx.lineWidth = width

      // Create curved path
      const midX = (from.x + to.x) / 2
      const midY = (from.y + to.y) / 2
      const offset = 30

      // Calculate control point offset
      const dx = to.x - from.x
      const dy = to.y - from.y
      const angle = Math.atan2(dy, dx) + Math.PI / 2

      const controlX = midX + Math.cos(angle) * offset
      const controlY = midY + Math.sin(angle) * offset

      // Draw path
      ctx.beginPath()
      ctx.moveTo(from.x, from.y)
      ctx.quadraticCurveTo(controlX, controlY, to.x, to.y)
      ctx.stroke()
    }

    // Draw particle
    const drawParticle = (ctx: CanvasRenderingContext2D, particle: DataParticle) => {
      const { connection, progress, size, opacity } = particle
      const { from, to } = connection

      // Calculate position along the curve
      const t = progress
      const midX = (from.x + to.x) / 2
      const midY = (from.y + to.y) / 2
      const offset = 30

      // Calculate control point offset
      const dx = to.x - from.x
      const dy = to.y - from.y
      const angle = Math.atan2(dy, dx) + Math.PI / 2

      const controlX = midX + Math.cos(angle) * offset
      const controlY = midY + Math.sin(angle) * offset

      // Calculate position on quadratic curve
      const x = Math.pow(1 - t, 2) * from.x + 2 * (1 - t) * t * controlX + Math.pow(t, 2) * to.x
      const y = Math.pow(1 - t, 2) * from.y + 2 * (1 - t) * t * controlY + Math.pow(t, 2) * to.y

      // Draw particle
      ctx.save()

      // Glow effect
      ctx.shadowColor = connection.color
      ctx.shadowBlur = 10

      // Draw circle
      ctx.fillStyle = connection.color
      ctx.globalAlpha = opacity
      ctx.beginPath()
      ctx.arc(x, y, size, 0, Math.PI * 2)
      ctx.fill()

      ctx.restore()
    }

    // Start animation
    animate()

    return () => {
      cancelAnimationFrame(animationFrame)
      window.removeEventListener("resize", setCanvasDimensions)
      window.removeEventListener("resize", createNodes)
    }
  }, [])

  return (
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 overflow-hidden">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="text-center mb-12"
      >
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
          Quantum AI Trading <span className="text-blue-500">Engine</span>
        </h2>
        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
          Our proprietary neural network processes market data in real-time to identify alpha opportunities before they
          appear on traditional charts.
        </p>
      </motion.div>

      <div className="relative h-[500px] rounded-xl overflow-hidden border border-gray-800 bg-gray-900/50 backdrop-blur-sm">
        {/* Modify the gradient overlay to be more subtle and only affect the grid */}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent pointer-events-none z-[1]" />
        
        <canvas ref={canvasRef} className="w-full h-full z-[2] relative" />

        <div className="absolute top-4 left-4 p-3 rounded-lg bg-gray-800/80 border border-gray-700/50 backdrop-blur-sm z-10">
          <div className="text-gray-300 text-xs font-medium">Market Status</div>
          <div className="mt-1 flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-green-400"></div>
            <div className="text-green-300 text-xs">Active</div>
          </div>
        </div>

        <div className="absolute bottom-4 right-4 p-3 rounded-lg bg-gray-800/80 border border-gray-700/50 backdrop-blur-sm z-10">
          <div className="text-gray-300 text-xs font-medium">Algorithm Confidence</div>
          <div className="mt-1 w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full w-4/5"></div>
          </div>
        </div>

        <div className="absolute top-4 right-4 p-3 rounded-lg bg-gray-800/80 border border-gray-700/50 backdrop-blur-sm z-10">
          <div className="text-gray-300 text-xs font-medium">Processing Speed</div>
          <div className="text-blue-400 text-sm font-bold">75ms</div>
        </div>

        <div className="absolute bottom-4 left-4 p-3 rounded-lg bg-gray-800/80 border border-gray-700/50 backdrop-blur-sm z-10">
          <div className="text-gray-300 text-xs font-medium">Data Points</div>
          <div className="text-blue-400 text-sm font-bold">4.2M+</div>
        </div>
      </div>
    </div>
  )
}

// TypeScript interfaces
interface Node {
  x: number
  y: number
  radius: number
  color: string
  type: "ai" | "data" | "output"  // Use literal union type instead of string
  connections: Connection[]
  pulsing: boolean
  pulseSize: number
  pulseOpacity: number
}

interface Connection {
  from: Node
  to: Node
  width: number
  color: string
  particles: DataParticle[]
}

interface DataParticle {
  connection: Connection
  progress: number
  speed: number
  size: number
  opacity: number
}

export default AIVisualization
